import React from 'react';
import Header from './shared/Header';
import Footer from './Footer';
import SEO from './SEO';
import {
  BuildingOfficeIcon,
  ClockIcon,
  ChartBarIcon,
  CogIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/20/solid';

const tampBenefits = [
  {
    name: 'Time Savings',
    icon: ClockIcon,
    description: 'Free up 40-50% of your time from portfolio management to focus on client planning and relationship building.',
    details: [
      "Automated trading and rebalancing",
      "Reduced administrative overhead",
      "More time for client meetings",
      "Focus on business development"
    ]
  },
  {
    name: 'Advanced Technology',
    icon: CogIcon,
    description: 'Access cutting-edge portfolio management tools and technology that would be cost-prohibitive to develop in-house.',
    details: [
      "Institutional-grade research",
      "Sophisticated risk management",
      "Real-time portfolio monitoring",
      "Advanced reporting capabilities"
    ]
  },
  {
    name: 'Enhanced Client Relationships',
    icon: UserGroupIcon,
    description: 'Build stronger client relationships by focusing on financial planning rather than portfolio management.',
    details: [
      "More client-facing time",
      "Better service delivery",
      "Increased client satisfaction",
      "Stronger retention rates"
    ]
  },
  {
    name: 'Growth & Succession Planning',
    icon: ArrowTrendingUpIcon,
    description: 'Build scalable processes that increase practice value and provide continuity during transitions.',
    details: [
      "Systematized investment process",
      "Reduced key person risk",
      "Easier practice transitions",
      "Increased practice value"
    ]
  }
];

const comparisonData = [
  {
    feature: 'Investment Approach',
    traditional: 'Manual research and trading',
    tamp: 'Systematic, automated strategies'
  },
  {
    feature: 'Rebalancing',
    traditional: 'Manual or calendar-based',
    tamp: 'Dynamic, signal-driven'
  },
  {
    feature: 'Technology',
    traditional: 'Basic tools or manual processes',
    tamp: 'Advanced portfolio management systems'
  },
  {
    feature: 'Research',
    traditional: 'Limited in-house capabilities',
    tamp: 'Institutional-grade research access'
  },
  {
    feature: 'Scalability',
    traditional: 'Limited by advisor capacity',
    tamp: 'Highly scalable operations'
  }
];

export default function WhatIsTampPage() {
  return (
    <>
      <SEO 
        title="What is a TAMP? | SharpeMetrix"
        description="Learn about Turnkey Asset Management Programs (TAMPs) and how they help financial advisors delegate investment management to focus on client relationships."
        keywords="TAMP, turnkey asset management program, financial advisors, portfolio management, investment delegation, advisor technology"
        url="/what-is-tamp"
      />
      <Header />
      <div className="min-h-screen bg-bg-100">
        <main className="relative isolate">
          {/* Background Gradient */}
          <div
            className="absolute inset-x-0 top-4 -z-10 flex transform-gpu justify-center overflow-hidden blur-3xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[1108/632] w-[69.25rem] flex-none bg-gradient-to-r from-[#305CFE] to-[#02D3FF] opacity-20"
              style={{
                clipPath:
                  'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
              }}
            />
          </div>

          {/* Header Section */}
          <div className="px-6 pt-14 lg:px-8">
            <div className="mx-auto max-w-3xl pt-24 text-center sm:pt-40 pb-16 sm:pb-24">
              <h1 className="text-4xl font-bold tracking-tight text-text-100 sm:text-6xl">
                What is a TAMP?
              </h1>
              <p className="mt-6 text-xl leading-8 text-text-200">
                Turnkey Asset Management Programs help financial advisors delegate investment research, portfolio construction, and trading automation to specialized RIAs.
              </p>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-24 sm:pb-32">

            {/* Overview Section */}
            <section className="mb-16 lg:mb-24">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                  <BuildingOfficeIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                  Understanding TAMPs
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  Financial advisors spend 25-28 hours per week with clients, with another 17-19 hours on preparation and back-office tasks. This leaves only 7-12 hours for new business development each week. TAMPs help advisors reclaim this time by automating trading, portfolio rebalancing, and investment research.
                </p>
              </div>
            </section>

            {/* TAMP Benefits Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 mb-16 lg:mb-24 ring-1 ring-white/10 shadow-lg">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                TAMP Benefits for Your Advisory Practice
              </h2>
              <div className="grid grid-cols-1 gap-x-8 gap-y-12 lg:grid-cols-2 lg:gap-y-16">
                {tampBenefits.map((benefit) => (
                  <div key={benefit.name} className="flex flex-col items-start">
                    <div className="flex items-center gap-x-3 mb-4">
                      <benefit.icon className="h-7 w-7 flex-none text-primary-100" aria-hidden="true" />
                      <h3 className="text-xl font-semibold leading-7 text-text-100">{benefit.name}</h3>
                    </div>
                    <p className="text-base leading-7 text-text-200 mb-3">{benefit.description}</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-text-300 pl-2">
                      {benefit.details.map((detail, index) => <li key={index}>{detail}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Comparison Section */}
            <section className="mb-16 lg:mb-24">
              <h2 className="text-3xl font-semibold leading-9 tracking-tight text-text-100 mb-10 text-center">
                TAMP vs Traditional Portfolio Management
              </h2>
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg bg-bg-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-bg-300">
                    <thead className="bg-bg-100">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-text-100">Feature</th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">Traditional Approach</th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">TAMP Solution</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bg-300 bg-bg-200">
                      {comparisonData.map((item, index) => (
                        <tr key={index}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-text-100">{item.feature}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-center">{item.traditional}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-center">{item.tamp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-bg-200/60 rounded-xl p-8 lg:p-12 ring-1 ring-white/10 shadow-lg">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl flex items-center justify-center gap-x-3">
                  <ChartBarIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                  Ready to Explore SharpeMetrix TAMP Solutions?
                </h2>
                <p className="mt-6 text-lg leading-8 text-text-200">
                  See how our systematic, rules-based approach differs from traditional TAMP providers and discover the SharpeMetrix advantage.
                </p>
                <div className="mt-10 flex items-center justify-center gap-x-6">
                  <a
                    href="/register"
                    className="inline-flex items-center gap-x-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                  >
                    Start Free 30-Day Trial
                  </a>
                  <a href="/tamp-comparison" className="text-sm font-semibold leading-6 text-text-200">
                    View All Comparisons <span aria-hidden="true">→</span>
                  </a>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
} 